import React, { memo, useMemo, useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Typography,
  useTheme,
  CircularProgress,
  Chip,
} from "@mui/material";
import FormModal from "./formModal";
import ResolutionStrategyCard from "./ResolutionStrategyCard";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { get } from "lodash";
import { useSelector } from "react-redux";
import { ReactComponent as KeyboardArrowDownIcon } from "../assets/icons/main/expand-down-icon.svg";
import { ReactComponent as KeyboardArrowUpIcon } from "../assets/icons/main/collapse-up-icon.svg";
import { ReactComponent as OverwriteIcon } from "../assets/icons/main/overwrite-icon.svg";
import { ReactComponent as SkipIcon } from "../assets/icons/main/skip-next-icon.svg";
import { ReactComponent as MergeIcon } from "../assets/icons/main/merge-icon.svg";
import CustomDataTable from "../components/CustomDataTable";
import { IconButton, Tooltip } from "@mui/material";

type DuplicatePair = {
  importedIndex: number;
  existingNodeId: string;
  importedAsset: any;
  existingAsset: any;
  componentOverlaps: string[];
};

type DuplicateStrategy = "overwrite" | "skip" | "merge";

const ASSET_LEVEL_DUPLICATE_COMPONENT_KEY = "__asset_level_duplicate__";

type FlowImportDuplicateModalProps = {
  open: boolean;
  strategy: DuplicateStrategy | null;
  duplicatePairs: DuplicatePair[];
  totalCandidateAssets: number;
  getExistingOptionsForPair: (pair: DuplicatePair) => any[];
  onStrategyChange: (value: DuplicateStrategy) => void;
  onClose: () => void;
  onContinue: (rowStrategies: Record<string, DuplicateStrategy>) => void;
  componentSchemaMap: Record<string, { ui_schema?: any; backend_schema?: any }>;
};

const strategyCards: Array<{
  key: DuplicateStrategy;
  title: string;
  description: string;
  badge: string;
  colorTheme: "red" | "blue" | "green";
}> = [
    {
      key: "overwrite",
      title: "Overwrite All",
      description: "Replace all existing assets with the new imported assets.",
      badge: "Existing data will be lost",
      colorTheme: "red",
    },
    {
      key: "skip",
      title: "Skip Existing",
      description: "Ignore the duplicate assets and import only new assets.",
      badge: "Existing data will be kept",
      colorTheme: "blue",
    },
    {
      key: "merge",
      title: "Merge",
      description:
        "Combine new asset configuration with existing where applicable.",
      badge: "Existing data will be updated",
      colorTheme: "green",
    },
  ];

const getAssetComponents = (asset: any): any[] =>
  get(asset, "data.options", []) as any[];

const normalizeComponentName = (value: any) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const getOptionComponentKey = (option: any) =>
  normalizeComponentName(
    option?.component_name ??
    option?.label ??
    option?.name ??
    option?.data?.name ??
    "",
  );

const normalizeOptionForDisplay = (option: any) => {
  if (!option || typeof option !== "object") return option;
  if (option.data && typeof option.data === "object") return option;

  const data: Record<string, any> = {};
  Object.entries(option).forEach(([key, value]) => {
    if (
      [
        "data",
        "component_name",
        "label",
        "name",
        "enabled",
        "operation",
        "component_id",
        "service_id",
        "uuid",
        "asset_id",
        "gateway_id",
        "ip",
        "id",
      ].includes(key)
    ) {
      return;
    }
    data[key] = value;
  });

  return {
    ...option,
    component_name:
      option?.component_name ?? option?.label ?? option?.name ?? "",
    data,
  };
};

const getDisplayNameByKey = (options: any[]) => {
  const byKey = new Map<string, string>();
  (options || []).forEach((o: any) => {
    const display = String(
      o?.component_name ?? o?.label ?? o?.name ?? o?.data?.name ?? "",
    ).trim();
    const key = normalizeComponentName(display);
    if (key && !byKey.has(key)) byKey.set(key, display);
  });
  return byKey;
};

const getDuplicateComponentsForPair = (
  pair: DuplicatePair,
  existingOptions: any[] = [],
): Array<{ key: string; displayName: string }> => {
  const importedOptions = getAssetComponents(pair.importedAsset);
  const displayNameByKey = getDisplayNameByKey([
    ...importedOptions,
    ...existingOptions,
  ]);

  const overlapKeys = Array.from(
    new Set(
      (pair.componentOverlaps || [])
        .map((name) => normalizeComponentName(name))
        .filter(Boolean),
    ),
  );

  const overlapComponents = overlapKeys
    .map((key) => ({ key, displayName: displayNameByKey.get(key) ?? key }))
    .filter((item) => Boolean(item.key));

  if (overlapComponents.length > 0) {
    return overlapComponents;
  }

  // If precomputed overlap names are missing, derive overlap from current local options.
  const existingKeys = new Set(
    (existingOptions || [])
      .map((opt: any) => getOptionComponentKey(opt))
      .filter(Boolean),
  );
  const derivedOverlap = (importedOptions || [])
    .map((opt: any) => getOptionComponentKey(opt))
    .filter((key) => key && existingKeys.has(key));
  const derivedOverlapComponents = Array.from(new Set(derivedOverlap)).map(
    (key) => ({
      key,
      displayName: displayNameByKey.get(key) ?? key,
    }),
  );
  if (derivedOverlapComponents.length > 0) {
    return derivedOverlapComponents;
  }

  // Fallback row keeps duplicate action path available when component details are not yet loaded.
  return [
    {
      key: ASSET_LEVEL_DUPLICATE_COMPONENT_KEY,
      displayName: "Asset-level duplicate",
    },
  ];
};

// Extract key from ui_schema scope e.g. "#/properties/tls_algo" → "tls_algo"
const scopeToKey = (scope: string): string => {
  const parts = scope.replace("#/", "").split("/");
  const propsIdx = parts.indexOf("properties");
  if (propsIdx !== -1 && parts[propsIdx + 1]) return parts[propsIdx + 1];
  return parts[parts.length - 1];
};

const safeParseSchema = (input: any) => {
  if (!input) return null;
  if (typeof input === "object") return input;
  if (typeof input !== "string") return null;

  try {
    return JSON.parse(input);
  } catch {
    return null;
  }
};

const resolveSecretTokenValue = (
  value: any,
  secretNameById: Map<string, string>,
) => {
  if (typeof value !== "string") return value;
  const token = String(value).trim();
  const match = token.match(/^secret:(\d+)$/i);
  if (!match) return value;
  return secretNameById.get(match[1]) ?? value;
};

const formatValue = (value: any): string => {
  if (value === null || value === undefined || value === "") return "N/A";
  if (typeof value === "boolean") return value ? "Enable" : "Disable";
  if (Array.isArray(value)) {
    if (!value.length) return "N/A";
    // Special case for scan_root-like arrays (mostly primitive values)
    if (value.every((v) => ["string", "number", "boolean"].includes(typeof v))) {
      return value.map((v) => String(v)).join(", ");
    }
    return `${value.length} item(s)`;
  }
  if (typeof value === "object") return "N/A";
  return String(value);
};

const getEnumDisplayValue = (value: any, schemaNode: any) => {
  if (value === null || value === undefined) return value;

  const variants = [
    ...(Array.isArray(schemaNode?.oneOf) ? schemaNode.oneOf : []),
    ...(Array.isArray(schemaNode?.anyOf) ? schemaNode.anyOf : []),
    ...(Array.isArray(schemaNode?.allOf) ? schemaNode.allOf : []),
  ];

  const match = variants.find(
    (variant: any) => String(variant?.const ?? "") === String(value),
  );

  return match?.title ?? match?.label ?? value;
};

const FIELD_LABEL_STYLE = {
  color: "#64759F",
  flexShrink: 0,
  whiteSpace: "nowrap" as const,
};
const FIELD_VALUE_STYLE = {
  color: "#434D67",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap" as const,
  minWidth: 0,
};
const FIELD_ROW_STYLE = {
  display: "flex",
  gap: 1,
  px: "24px",
  py: "8px",
  borderBottom: "1px solid #D9DFED40",
  alignItems: "flex-start",
  overflow: "hidden",
};

const FieldRow = ({ label, value }: { label: string; value: any }) => (
  <Box sx={FIELD_ROW_STYLE}>
    <Typography variant="subtitle1" sx={FIELD_LABEL_STYLE}>
      {label}:
    </Typography>
    <Typography variant="subtitle1" sx={FIELD_VALUE_STYLE}>
      {formatValue(value)}
    </Typography>
  </Box>
);

// Two-column field pair inside a row (like image 2)
const FieldPairRow = ({
  fields,
}: {
  fields: Array<{ label: string; value: any }>;
}) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: `repeat(${fields.length}, 1fr)`,
      borderBottom: "1px solid #D9DFED40",
    }}
  >
    {fields.map((f, i) => (
      <Box
        key={i}
        sx={{
          display: "flex",
          gap: 1,
          px: "24px",
          py: "8px",
          alignItems: "flex-start",
        }}
      >
        <Typography variant="subtitle1" sx={FIELD_LABEL_STYLE}>
          {f.label}:
        </Typography>
        <Typography variant="subtitle1" sx={FIELD_VALUE_STYLE}>
          {formatValue(f.value)}
        </Typography>
      </Box>
    ))}
  </Box>
);

// Render messages section as grouped blocks matching image 2
const renderMessages = (
  messages: any[],
  parentLabel: string,
  detailElements: any[],
  messageSchemaProperties: Record<string, any> = {},
): React.ReactNode => {
  if (!Array.isArray(messages) || messages.length === 0) return null;

  // Collect sub-field labels from ui_schema detail elements (if available)
  const getSubLabel = (key: string): string => {
    if (detailElements?.length) {
      const el = detailElements.find(
        (e: any) => scopeToKey(e.scope ?? "") === key,
      );
      if (el?.label) return el.label;
    }
    // Fallback friendly names
    const names: Record<string, string> = {
      id: "Message name",
      name: "Message name",
      type: "Message type",
      refresh_interval: "Refresh Interval for polling",
      trigger: "Read",
      comparison: "When",
      value: "Is",
    };
    return names[key] ?? key;
  };

  return (
    <Box>
      {messages.map((msg: any, msgIdx: number) => (
        <Box key={msgIdx} sx={{ borderBottom: "1px solid #D9DFED80" }}>
          {/* Name + Type pair */}
          <FieldPairRow
            fields={[
              { label: `${parentLabel} Name`, value: msg.id ?? msg.name },
              {
                label: `${parentLabel} Type`,
                value: getEnumDisplayValue(msg.type, messageSchemaProperties.type),
              },
            ]}
          />
          {/* Periodic: refresh_interval */}
          {msg.type === "periodic" && msg.refresh_interval !== undefined && (
            <FieldRow
              label={getSubLabel("refresh_interval")}
              value={msg.refresh_interval}
            />
          )}
          {/* Trigger: Read + When pair */}
          {msg.type === "trigger" && (
            <>
              <FieldPairRow
                fields={[
                  { label: getSubLabel("trigger"), value: msg.trigger },
                  { label: getSubLabel("comparison"), value: msg.comparison },
                ]}
              />
              {msg.value !== undefined && (
                <FieldRow label={getSubLabel("value")} value={msg.value} />
              )}
            </>
          )}
          {/* Props: ID + Key + UOM in 3 columns */}
          {Array.isArray(msg.props) &&
            msg.props.length > 0 &&
            msg.props.map((prop: any, pIdx: number) => (
              <FieldPairRow
                key={pIdx}
                fields={[
                  { label: "Messages ID", value: prop.id },
                  { label: "Messages Key", value: prop.key },
                  { label: "Messages UOM", value: prop.uom },
                ]}
              />
            ))}
        </Box>
      ))}
    </Box>
  );
};

// Render fields using ui_schema elements, reading values from `data`
const renderSchemaFields = (
  uiElements: any[],
  data: any,
  secretFieldKeys: Set<string>,
  secretNameById: Map<string, string>,
  backendProperties: Record<string, any> = {},
): React.ReactNode[] => {
  if (!uiElements?.length || !data) return [];
  const HIDDEN_DISPLAY_KEYS = new Set([
    "copy_configuration_from"
  ]);
  return uiElements
    .map((element: any, idx: number) => {
      if (element.type !== "Control") return null;
      const key = scopeToKey(element.scope ?? "");
      if (HIDDEN_DISPLAY_KEYS.has(key)) return null;
      const label = element.label || key;
      const value = get(data, key);
      const token = String(value ?? "").trim();
      const shouldResolve =
        secretFieldKeys.has(key) || /^secret:\d+$/i.test(token);

      const displayValue = shouldResolve
        ? resolveSecretTokenValue(value, secretNameById)
        : value;

      // Array field with detail schema (e.g. messages)
      const detailElements = get(element, "options.detail.elements", null);
      // const messageSchemaProperties =
      //   key === "messages"
      //     ? get(data, "__messageSchemaProperties", null)
      //     : null;

      if (Array.isArray(displayValue)) {
        // messages array -> keep existing message renderer
        if (key === "messages") {
          return (
            <Box key={idx}>
              {renderMessages(
                displayValue,
                label,
                detailElements ?? [],
                get(backendProperties, "messages.items.properties", {}),
              )}
            </Box>
          );
        }

        // generic array field (like scan_root)
        const printable = displayValue
          .map((item: any) => {
            if (item === null || item === undefined) return "";
            if (typeof item === "string" || typeof item === "number" || typeof item === "boolean") {
              return String(item);
            }
            if (typeof item === "object") {
              // pick common value keys first
              return String(
                item.value ?? item.name ?? item.id ?? item.key ?? item.label ?? ""
              ).trim();
            }
            return "";
          })
          .filter(Boolean)
          .join(", ");

        return <FieldRow key={idx} label={label} value={printable || "N/A"} />;
      }

      return <FieldRow key={idx} label={label} value={displayValue} />;
    })
    .filter(Boolean);
};

// Fallback: render raw data fields when no ui_schema is available (imported assets)
const renderRawDataFields = (
  data: any,
  secretFieldKeys: Set<string>,
  secretNameById: Map<string, string>,
  messageSchemaProperties: Record<string, any> = {},
): React.ReactNode[] => {
  if (!data || typeof data !== "object") return [];
  const SKIP_KEYS = new Set(["messages", "services", "secret_id_array", "copy_configuration_from"]);
  const nodes: React.ReactNode[] = [];

  Object.entries(data).forEach(([key, value]: [string, any]) => {
    if (SKIP_KEYS.has(key)) return;
    // Friendly label from key
    const label = key
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
    const token = String(value ?? "").trim();
    const shouldResolveSecret =
      secretFieldKeys.has(key) || /^secret_/i.test(key) || /^secret:\d+$/i.test(token);
    const displayValue = shouldResolveSecret
      ? resolveSecretTokenValue(value, secretNameById)
      : value;
    nodes.push(<FieldRow key={key} label={label} value={displayValue} />);
  });

  if (Array.isArray(data.messages) && data.messages.length > 0) {
    nodes.push(
      <Box key="__messages">
        {renderMessages(data.messages, "Messages configured", [], messageSchemaProperties)}
      </Box>,
    );
  }

  return nodes;
};

const ComponentDetailPanel = ({
  option,
  schemaOption, // use this option's ui_schema as template (falls back to option itself)
  assetEnabled,
  isLoading,
  secretNameById,
}: {
  option: any;
  schemaOption?: any;
  assetEnabled: boolean;
  isLoading: boolean;
  secretNameById: Map<string, string>;
}) => {
  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: "24px",
        }}
      >
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (!option) {
    return (
      <Box sx={FIELD_ROW_STYLE}>
        <Typography variant="subtitle1" sx={{ color: "#64759F" }}>
          No component data available
        </Typography>
      </Box>
    );
  }

  const data = get(option, "data", {});
  const schemaSource = schemaOption ?? option;
  const uiElements = get(schemaSource, "ui_schema.elements", []);
  const backendSchema = safeParseSchema(get(schemaSource, "backend_schema"));
  const backendProperties = get(backendSchema, "properties", {});
  const secretFieldKeys = new Set(
    Object.keys(backendProperties).filter(
      (fieldKey) => get(backendProperties, `${fieldKey}.secret`) === true,
    ),
  );

  return (
    <Box>
      <FieldRow label="Asset Status" value={assetEnabled} />
      <FieldRow
        label="Component Name"
        value={get(option, "component_name", "N/A")}
      />
      <FieldRow
        label="Component Status"
        value={get(option, "enabled", false)}
      />
      {uiElements.length > 0
        ? renderSchemaFields(
          uiElements,
          data,
          secretFieldKeys,
          secretNameById,
          backendProperties,
        )
        : renderRawDataFields(
          data,
          secretFieldKeys,
          secretNameById,
          get(backendProperties, "messages.items.properties", {}),
        )
      }
    </Box>
  );
};

const AssetComparisonPanel = ({
  existingAsset,
  importedAsset,
  componentName,
  componentKey,
  isLoadingExisting = false,
  secretNameById,
  componentSchemaMap
}: {
  existingAsset: any;
  importedAsset: any;
  componentName: string;
  componentKey: string;
  isLoadingExisting?: boolean;
  secretNameById: Map<string, string>;
  componentSchemaMap: Record<string, { ui_schema?: any; backend_schema?: any }>;
}) => {
  const existingOptions = getAssetComponents(existingAsset);
  const importedOptions = getAssetComponents(importedAsset);
  const schemaByName =
    componentSchemaMap[normalizeComponentName(componentKey)] || {};

  if (componentKey === ASSET_LEVEL_DUPLICATE_COMPONENT_KEY) {
    const existingAssetEnabled = Boolean(
      get(existingAsset, "data.enabled", true),
    );
    const importedAssetEnabled = Boolean(
      get(importedAsset, "data.enabled", true),
    );


    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: "24px",
          p: "24px 32px",
        }}
      >
        <Box
          sx={{
            border: "1px solid #D9DFED80",
            borderRadius: 0,
            overflow: "hidden",
            backgroundColor: "#fff",
            flex: 1,
          }}
        >
          <Box
            sx={{
              py: "14px",
              px: "24px",
              backgroundColor: "#537EFF1A",
              borderBottom: "1px solid #D9DFED80",
            }}
          >
            <Typography variant="subtitle2" sx={{ color: "#434D67" }}>
              Existing Asset Configuration
            </Typography>
          </Box>
          <Box>
            <FieldRow label="Asset Status" value={existingAssetEnabled} />
            <FieldRow
              label="Component Name"
              value="N/A (asset-level fallback)"
            />
          </Box>
        </Box>

        <Box
          sx={{
            border: "1px solid #D9DFED80",
            borderRadius: 0,
            overflow: "hidden",
            backgroundColor: "#fff",
            flex: 1,
          }}
        >
          <Box
            sx={{
              py: "14px",
              px: "24px",
              backgroundColor: "#0EAB4E1A",
              borderBottom: "1px solid #D9DFED80",
            }}
          >
            <Typography variant="subtitle2" sx={{ color: "#434D67" }}>
              Importing Asset Configuration
            </Typography>
          </Box>
          <Box>
            <FieldRow label="Asset Status" value={importedAssetEnabled} />
            <FieldRow
              label="Component Name"
              value="N/A (asset-level fallback)"
            />
          </Box>
        </Box>
      </Box>
    );
  }

  const isSameComponent = (option: any) =>
    getOptionComponentKey(option) === normalizeComponentName(componentKey);

  const existingOption = normalizeOptionForDisplay(
    existingOptions.find((o: any) => isSameComponent(o)),
  );
  const importedOption = normalizeOptionForDisplay(
    importedOptions.find((o: any) => isSameComponent(o)),
  );

  const existingAssetEnabled = Boolean(
    get(existingAsset, "data.enabled", true),
  );
  const importedAssetEnabled = Boolean(
    get(importedAsset, "data.enabled", true),
  );

  // Use existingOption's ui_schema as the canonical schema for both panels
  // (imported options from Excel don't carry ui_schema)
  const canonicalSchemaOption = existingOption ?? importedOption;

  const panelStyle = {
    border: "1px solid #D9DFED80",
    borderRadius: 0,
    overflow: "hidden",
    backgroundColor: "#fff",
    flex: 1,
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        gap: "24px",
        p: "24px 32px",
      }}
    >
      {/* Existing */}
      <Box sx={panelStyle}>
        <Box
          sx={{
            py: "14px",
            px: "24px",
            backgroundColor: "#537EFF1A",
            borderBottom: "1px solid #D9DFED80",
          }}
        >
          <Typography variant="subtitle2" sx={{ color: "#434D67" }}>
            Existing Asset Configuration
          </Typography>
        </Box>
        <ComponentDetailPanel
          option={existingOption}
          // schemaOption={canonicalSchemaOption}
          schemaOption={{ ...existingOption, ...schemaByName }}
          assetEnabled={existingAssetEnabled}
          isLoading={isLoadingExisting}
          secretNameById={secretNameById}
        />
      </Box>

      {/* Importing */}
      <Box sx={panelStyle}>
        <Box
          sx={{
            py: "14px",
            px: "24px",
            backgroundColor: "#0EAB4E1A",
            borderBottom: "1px solid #D9DFED80",
          }}
        >
          <Typography variant="subtitle2" sx={{ color: "#434D67" }}>
            Importing Asset Configuration
          </Typography>
        </Box>
        <ComponentDetailPanel
          option={importedOption}
          // schemaOption={canonicalSchemaOption}
          schemaOption={{ ...importedOption, ...schemaByName }}
          assetEnabled={importedAssetEnabled}
          isLoading={false}
          secretNameById={secretNameById}
        />
      </Box>
    </Box>
  );
};

const PAGE_SIZE = 10;

const getStrategyChipMeta = (strategy: DuplicateStrategy | null) => {
  if (!strategy) {
    return {
      label: "No Action",
      colors: {
        bg: "#EEF3FF",
        border: "#8AA3D6",
        text: "#5E76A8",
      },
    };
  }

  if (strategy === "overwrite") {
    return {
      label: "Overwrite",
      colors: {
        bg: "#FFEAEA",
        border: "#E06A6A",
        text: "#B13A3A",
      },
    };
  }

  if (strategy === "merge") {
    return {
      label: "Merge",
      colors: {
        bg: "#E8F8EF",
        border: "#66B38A",
        text: "#2C7A52",
      },
    };
  }

  return {
    label: "Skip",
    colors: {
      bg: "#FFF4E6",
      border: "#D29A4C",
      text: "#A06B22",
    },
  };
};

const FlowImportDuplicateModal = ({
  open,
  strategy,
  duplicatePairs,
  totalCandidateAssets,
  componentSchemaMap,
  getExistingOptionsForPair,
  onStrategyChange,
  onClose,
  onContinue,
}: FlowImportDuplicateModalProps) => {
  const themeData: any = useTheme();
  const appIconColor = get(themeData, "palette.custom.light", "#51648F");

  const secretsData = useSelector(
    (state: any) => state.secretsManager.secretsData,
  );

  const secretNameById = useMemo(
    () =>
      new Map<string, string>(
        (secretsData || [])
          .map(
            (secret: any) =>
              [
                String(secret?.secret_id ?? "").trim(),
                String(secret?.secret_name ?? "").trim(),
              ] as const,
          )
          .filter(
            ([secretId, secretName]: readonly [string, string]) =>
              Boolean(secretId) && Boolean(secretName),
          ),
      ),
    [secretsData],
  );

  const uniquePairs = useMemo(
    () =>
      Array.from(
        new Map(
          duplicatePairs.map((pair) => [pair.importedIndex, pair]),
        ).values(),
      ),
    [duplicatePairs],
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [expandedRows, setExpandedRows] = useState<Array<string | number>>([]);
  const [rowStrategies, setRowStrategies] = useState<
    Record<string, DuplicateStrategy>
  >({});


  const handleToggleRow = useCallback((id: string | number) => {
    setExpandedRows((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : [id],
    );
  }, []);

  const handleRowStrategy = (rowId: string, val: DuplicateStrategy) =>
    setRowStrategies((prev) => ({ ...prev, [rowId]: val }));

  const allTableRows = useMemo(() => {
    const rows: any[] = [];
    uniquePairs.forEach((pair) => {
      const assetName = String(
        get(pair, "existingAsset.data.name", "") ||
        get(pair, "importedAsset.data.name", "") ||
        "—",
      ).trim();

      const existingAssetIp =
        String(get(pair, "existingAsset.data.ip", "")).trim() || "—";
      const importingAssetIp =
        String(get(pair, "importedAsset.data.ip", "")).trim() || "—";

      const duplicateComponents = getDuplicateComponentsForPair(
        pair,
        getExistingOptionsForPair(pair),
      );

      // Create one row per component
      duplicateComponents.forEach((component) => {
        const rowId = `${pair.importedIndex}-${component.key}`;
        rows.push({
          id: rowId,
          asset_name: assetName,
          asset_ip: existingAssetIp,
          importing_asset_ip: importingAssetIp,
          component_name: component.displayName,
          component_key: component.key,
          _pair: pair,
          _rowId: rowId,
        });
      });
    });
    return rows;
  }, [uniquePairs, getExistingOptionsForPair]);

  const validRowIds = useMemo(() => {
    return new Set(allTableRows.map((row: any) => String(row.id)));
  }, [allTableRows]);

  useEffect(() => {
    // Keep only row actions belonging to currently visible rows.
    setRowStrategies((prev) => {
      let changed = false;
      const next: Record<string, DuplicateStrategy> = {};

      Object.entries(prev).forEach(([key, val]) => {
        if (validRowIds.has(key)) {
          next[key] = val;
        } else {
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [validRowIds]);

  const tableRows = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    const paginatedRows = allTableRows.slice(start, start + PAGE_SIZE);

    // Add dynamic accordion component resolved from local renderer state.
    return paginatedRows.map((row: any) => ({
      ...row,
      accordianComponent: (() => {
        const pair = row._pair;
        const localOptions = getExistingOptionsForPair(pair);
        const enrichedAsset =
          localOptions.length > 0
            ? {
              ...pair.existingAsset,
              data: {
                ...pair.existingAsset.data,
                options: localOptions,
              },
            }
            : pair.existingAsset;

        return (
          <AssetComparisonPanel
            existingAsset={enrichedAsset}
            importedAsset={pair.importedAsset}
            componentName={row.component_name}
            componentKey={row.component_key}
            isLoadingExisting={false}
            secretNameById={secretNameById}
            componentSchemaMap={componentSchemaMap}
          />
        );
      })(),
    }));
  }, [allTableRows, currentPage, getExistingOptionsForPair, secretNameById]);

  const getEffectiveRowStrategy = (row: any): DuplicateStrategy | null => {
    const rowId = String(get(row, "id", ""));
    return rowStrategies[rowId] ?? strategy ?? null;
  };

  const unresolvedRowsCount = useMemo(
    () =>
      allTableRows.filter((row: any) => {
        const rowId = String(get(row, "id", ""));
        return !(rowStrategies[rowId] ?? strategy);
      }).length,
    [allTableRows, rowStrategies, strategy],
  );

  const tableColumns = useMemo(
    () => [
      {
        field: "asset_name",
        headerName: "Asset Name",
        sortable: false,
        headerAlign: "left",
        align: "left",
        minWidth: 250,
        renderCell: ({ value }: any) => (
          <Typography
            component="p"
            variant="subtitle1"
            sx={{
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
          >
            {value}
          </Typography>
        ),
      },
      {
        field: "asset_ip",
        headerName: "Asset IP",
        sortable: false,
        headerAlign: "left",
        align: "left",
        minWidth: 200,
        renderCell: ({ value }: any) => (
          <Typography
            component="p"
            variant="subtitle1"
            sx={{
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
          >
            {value}
          </Typography>
        ),
      },
      {
        field: "importing_asset_ip",
        headerName: "Importing Asset IP",
        sortable: false,
        headerAlign: "left",
        align: "left",
        minWidth: 200,
        renderCell: ({ value }: any) => (
          <Typography
            component="p"
            variant="subtitle1"
            sx={{
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
          >
            {value}
          </Typography>
        ),
      },
      {
        field: "component_name",
        headerName: "Component Name",
        sortable: false,
        headerAlign: "left",
        align: "left",
        minWidth: 250,
        renderCell: ({ value }: any) => (
          <Typography
            component="p"
            variant="subtitle1"
            sx={{
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
          >
            {value}
          </Typography>
        ),
      },
      {
        field: "status",
        headerName: "Status",
        sortable: false,
        headerAlign: "left",
        align: "left",
        minWidth: 200,
        renderCell: ({ row }: any) => {
          const chipMeta = getStrategyChipMeta(getEffectiveRowStrategy(row));
          return (
            <Chip
              label={chipMeta.label}
              size="small"
              variant="outlined"
              sx={{
                borderRadius: "18px",
                fontWeight: 600,
                backgroundColor: chipMeta.colors.bg,
                borderColor: chipMeta.colors.border,
                color: chipMeta.colors.text,
                "& .MuiChip-label": {
                  px: 1.5,
                },
              }}
            />
          );
        },
      },
      {
        field: "actions",
        headerName: "ACTION",
        sortable: false,
        headerAlign: "right",
        align: "right",
        minWidth: 320,
        renderCell: ({ row }: any) => {
          const rowId = String(get(row, "id", ""));
          const selectedAction = rowStrategies[rowId] ?? null;
          const iconActions: Array<{
            key: DuplicateStrategy;
            label: string;
            Icon: React.FC<React.SVGProps<SVGSVGElement>>;
          }> = [
              { key: "overwrite", label: "Overwrite", Icon: OverwriteIcon },
              { key: "skip", label: "Skip", Icon: SkipIcon },
              { key: "merge", label: "Merge", Icon: MergeIcon },
            ];
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "16px" }}>
                {iconActions.map(({ key, label, Icon }) => {
                  return (
                    <Tooltip key={key} title={label} placement="top">
                      <IconButton
                        size="small"
                        onClick={() => handleRowStrategy(rowId, key)}
                        sx={{
                          p: "4px",
                          borderRadius: "4px",
                          backgroundColor:
                            selectedAction === key ? "#EEF3FF" : "transparent",
                          "&:hover": {
                            backgroundColor:
                              selectedAction === key ? "#E6EEFF" : "#F5F7FB",
                          },
                          "& svg": {
                            width: 24,
                            height: 24,
                            color:
                              selectedAction === key ? "#4F74C8" : appIconColor,
                          },
                        }}
                      >
                        <Icon />
                      </IconButton>
                    </Tooltip>
                  );
                })}
              </Box>
              <Box
                sx={{
                  width: "1px",
                  height: 20,
                  backgroundColor: "#D9DFED",
                  mx: "24px",
                }}
              />
              <Box
                component="span"
                onClick={() => handleToggleRow(row.id)}
                sx={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  p: 0.5,
                }}
              >
                {expandedRows.includes(row.id) ? (
                  <KeyboardArrowUpIcon style={{ color: appIconColor }} />
                ) : (
                  <KeyboardArrowDownIcon style={{ color: appIconColor }} />
                )}
              </Box>
            </Box>
          );
        },
      },
    ],
    [rowStrategies, expandedRows, strategy, appIconColor],
  );

  return (
    <FormModal
      title="DUPLICATE ASSETS DETECTED"
      isOpen={open}
      handleClose={onClose}
      hideButtons
      customStyles={{
        maxWidth: "1856px",
        width: "min(1856px, calc(100vw - 40px))",
        minHeight: "90vh",
      }}
      containerStyles={{
        "& .MuiCard-root": { overflowX: "hidden !important" },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Typography
          variant="subtitle1"
          sx={{ color: themeData.palette.text.secondary }}
        >
          The selected assets already exist in the system. Choose how to proceed
          with duplicates.
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            p: "12px",
            borderRadius: "4px",
            backgroundColor: "#FF88001A",
            maxHeight: "46px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px",
            }}
          >
            <WarningAmberRoundedIcon
              sx={{ color: "#f59e0b", fontSize: 16, display: "block" }}
            />
            <Typography
              variant="caption"
              sx={{
                color: "#434D67",
                fontWeight: 600,
                letterSpacing: "0.02em",
                textAlign: "center",
                lineHeight: 1,
              }}
            >
              {`${uniquePairs.length} duplicate assets found in this import out of ${totalCandidateAssets} total assets`}
            </Typography>
          </Box>
        </Box>

        <Typography
          variant="subtitle1"
          sx={{ color: themeData.palette.text.secondary }}
        >
          Choose an action for duplicate assets
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
            gap: 2,
          }}
        >
          {strategyCards.map((item) => (
            <ResolutionStrategyCard
              key={item.key}
              value={item.key}
              selected={strategy === item.key}
              title={item.title}
              description={item.description}
              badge={item.badge}
              colorTheme={item.colorTheme}
              onChange={(val) => onStrategyChange(val as DuplicateStrategy)}
            />
          ))}
        </Box>

        {unresolvedRowsCount > 0 && (
          <Typography
            variant="caption"
            sx={{ color: themeData.palette.warning.main }}
          >
            Select a global action or set row actions for all duplicate assets
            before continuing.
          </Typography>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{ color: themeData.palette.text.secondary }}
          >
            Review Duplicate Assets:
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: themeData.palette.text.secondary }}
          >
            Compare existing assets configuration in the system with importing
            assets configuration
          </Typography>
        </Box>

        {/* Duplicate assets table using CustomDataTable */}
        <Box
          sx={{
            border: "1px solid #e2e8f0",
            borderRadius: "4px",
            overflow: "hidden",
            "& .MuiTableBody-root .MuiTableRow-root > td[colspan]": {
              padding: "0 !important",
            },
            "& .MuiTableBody-root .MuiCollapse-root .MuiCollapse-wrapperInner > .MuiBox-root":
            {
              margin: "0 !important",
            },
          }}
        >
          <CustomDataTable
            rows={tableRows}
            columns={tableColumns}
            isExpandable
            expandedRows={expandedRows}
            isServerPagination
            totalRowCount={allTableRows.length}
            rowsPerPage={PAGE_SIZE}
            page={currentPage}
            onPagination={(_pageSize: number, pageNumber: number) => {
              setCurrentPage(pageNumber);
              setExpandedRows([]);
            }}
            noRowsLabel="No duplicate assets to review."
            getRowId={(row: any) => row.id}
          />
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center", gap: 3 }}>
          <Button variant="outlined" onClick={onClose}>
            CANCEL IMPORT
          </Button>
          <Button
            variant="contained"
            onClick={() => onContinue(rowStrategies)}
            disabled={unresolvedRowsCount > 0}
          >
            CONTINUE
          </Button>
        </Box>
      </Box>
    </FormModal>
  );
};

export default memo(FlowImportDuplicateModal);
